import PropTypes from 'prop-types';
import Label from '../../../components/label';
import formatDate from '../../../utils/formatDate';

export const boldHeader = (color) => (table) => <b style={{ color }}>{table?.column?.columnDef?.header}</b>;

// ----------------------------------------------------------------------
// Dropdown option sets (shared by the form + filters)
// ----------------------------------------------------------------------

export const ASSET_STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'under_repair', label: 'Under Repair' },
  { value: 'retired', label: 'Retired' },
  { value: 'lost', label: 'Lost' },
];

export const ASSET_CONDITION_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'damaged', label: 'Damaged' },
];

// Maps an asset status to a MUI Label colour.
export const ASSET_STATUS_COLOR = {
  available: 'success',
  assigned: 'info',
  under_repair: 'warning',
  retired: 'default',
  lost: 'error',
};

export const getStatusLabel = (value) =>
  ASSET_STATUS_OPTIONS.find((option) => option.value === value)?.label || value || 'N/A';

export const getConditionLabel = (value) =>
  ASSET_CONDITION_OPTIONS.find((option) => option.value === value)?.label || value || 'N/A';

export const StatusBadge = ({ status }) => (
  <Label color={ASSET_STATUS_COLOR[status] || 'default'}>{getStatusLabel(status)}</Label>
);

StatusBadge.propTypes = {
  status: PropTypes.string,
};

export const ASSET_LIST_TABLE_COLUMNS = [
  {
    accessorKey: 'name',
    header: 'Asset Name',
    Header: boldHeader('#454F5B'),
    size: 50,
    Cell: ({ cell }) => <span style={{ textTransform: 'capitalize' }}>{cell.getValue()}</span>,
  },
  {
    accessorKey: 'type',
    header: 'Asset Type',
    Header: boldHeader('#454F5B'),
    size: 50,
    Cell: ({ cell }) => <span style={{ textTransform: 'capitalize' }}>{cell.getValue()}</span>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    Header: boldHeader('#454F5B'),
    size: 50,
    enableColumnFilter: false,
    Cell: ({ cell }) => <StatusBadge status={cell.getValue()} />,
  },
  {
    accessorKey: 'category',
    header: 'Category',
    Header: boldHeader('#454F5B'),
    size: 50,
    Cell: ({ cell }) => <span style={{ textTransform: 'capitalize' }}>{cell.getValue() || 'N/A'}</span>,
  },
  {
    accessorKey: 'serialNumber',
    header: 'Serial No.',
    Header: boldHeader('#454F5B'),
    size: 50,
    Cell: ({ cell }) => <span>{cell.getValue() || 'N/A'}</span>,
  },
  {
    accessorKey: 'location',
    header: 'Location',
    Header: boldHeader('#454F5B'),
    size: 50,
    Cell: ({ cell }) => <span style={{ textTransform: 'capitalize' }}>{cell.getValue() || 'N/A'}</span>,
  },
  {
    accessorKey: 'warrantyExpiry',
    header: 'Warranty Expiry',
    Header: boldHeader('#454F5B'),
    size: 50,
    enableColumnFilter: false,
    Cell: ({ cell }) => <span>{formatDate(cell.getValue())}</span>,
  },
  {
    accessorKey: 'assignedUserName',
    header: 'Assigned To',
    Header: boldHeader('#454F5B'),
    size: 50,
    Cell: ({ cell }) => <span style={{ textTransform: 'capitalize' }}>{cell.getValue() || 'Unassigned'}</span>,
  },
];

export const ASSET_LIST_TABLE_DEFAULT_PROPS = {
  positionActionsColumn: 'last',
  enableFullScreenToggle: false,
  enableColumnActions: false,
  manualPagination: true,
  manualFiltering: true,
  manualSorting: true,
  initialState: {
    density: 'compact', // or 'standard' or 'compact'
    pagination: { pageSize: 10, pageIndex: 0 },
  },
  muiTablePaperProps: {
    sx: {
      color: 'rgb(71 85 105 / var(--tw-text-opacity))',
    },
  },
  muiTopToolbarProps: {
    sx: {
      backgroundColor: '#fff',
    },
  },
  muiTableHeadRowProps: {
    sx: {
      '--tw-bg-opacity': 1,
      backgroundColor: 'rgb(226 232 240 / var(--tw-bg-opacity))',
      fontWeight: '900',
    },
  },
  muiTableHeadCellFilterTextFieldProps: {
    sx: {
      m: '0.5rem 0',
      borderColor: 'white',
      '& input': {
        borderColor: 'white',
        fontSize: '0.05rem',
        '&::placeholder': {
          color: 'white',
          fontSize: '0.05rem',
        },
      },
      '& .MuiInput-root:before, &.MuiInputBase-root:before': {
        borderBottomColor: 'white',
        color: 'white',
      },
      '& .MuiInputBase-input, &.MuiButtonBase-root, &.MuiIconButton-root': {
        textTransform: 'capitalize',
        color: 'white',
        fontSize: '0.05rem',
      },
    },
    '& .MuiInput-input': {
      font: 'unset !important', // Remove the inherited font
      fontSize: '12px !important', // Set the desired font size
      padding: '0.25rem 0 !important',
      color: 'white !important',
      '&::placeholder': {
        color: 'white !important',
        opacity: '0.7 !important',
        fontSize: '12px !important',
      },
    },
  },
  muiTableHeadCellProps: {
    sx: {
      backgroundColor: '#F9FAFB',
      margin: 'auto',
      fontWeight: '800',
      fontSize: '.85rem',
      lineHeight: '2rem',
      textTransform: 'capitalize',
      '--tw-text-opacity': 1,
      borderColor: '#F9FAFB',
      paddingRight: '1.5rem',
      paddingBottom: '1.25rem',
      paddingLeft: '1.5rem',
      ' & .Mui-TableHeadCell-Content': {
        position: 'relative',
        top: '5px',
      },
    },
  },
  muiTableBodyCellProps: {
    sx: {
      '--tw-border-opacity': 1,
      borderColor: 'rgb(241 245 249 / var(--tw-border-opacity))',
      '--tw-text-opacity': 1,
      paddingLeft: '1.5rem',
    },
  },
};
